import React, { useState, useRef, useEffect } from 'react';
import api from '../services/api';

const initialMessages = [
  {
    id: 'welcome',
    role: 'assistant',
    type: 'text',
    text:
      'Olá! Eu sou o Consultor de Cobrança. Posso te mostrar, por exemplo:\n\n' +
      '- Top clientes que mais devem\n' +
      '- Top clientes com mais contas vencidas\n' +
      '- Pareto (20% dos clientes que respondem por ~80% da dívida)\n\n' +
      'Use os botões rápidos abaixo ou digite sua pergunta.',
  },
];

function AnalyticsChat() {
  const [messages, setMessages] = useState(initialMessages);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const addMessage = (msg) => {
    setMessages((prev) => [...prev, { id: `${Date.now()}-${Math.random()}`, ...msg }]);
  };

  const formatValue = (value, format) => {
    if (format === 'currency') {
      const num = Number(value || 0);
      return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    }
    if (format === 'percent') {
      const num = Number(value || 0);
      return `${num.toFixed(1)}%`;
    }
    return value;
  };

  const runAnalysis = async ({ userText, endpoint, title, columns, isPareto = false }) => {
    addMessage({ role: 'user', type: 'text', text: userText });
    setLoading(true);
    try {
      const response = await api.get(endpoint);
      const payload = response.data || {};
      const rows = payload.results || [];

      addMessage({
        role: 'assistant',
        type: 'analysis',
        title,
        columns,
        rows,
        meta: isPareto
          ? {
              totalGeral: payload.total_geral,
              cobertoPercentual: payload.coberto_percentual,
            }
          : null,
      });
    } catch (error) {
      console.error(error);
      addMessage({
        role: 'assistant',
        type: 'text',
        text:
          'Não consegui gerar essa análise agora. Tente novamente mais tarde ou fale com o administrador.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleQuickTopDevedores = () =>
    runAnalysis({
      userText: 'Quais são os 10 clientes que mais devem em valor total?',
      endpoint: '/analytics/top_devedores/?limit=10',
      title: 'Top 10 clientes que mais devem (valor em aberto)',
      columns: [
        { key: 'nome', label: 'Cliente' },
        { key: 'total_aberto', label: 'Total em aberto (R$)', format: 'currency' },
        { key: 'contas_em_aberto', label: 'Qtde contas em aberto' },
        { key: 'valor_em_atraso', label: 'Valor em atraso (R$)', format: 'currency' },
        { key: 'percentual_inadimplencia', label: '% inadimplência', format: 'percent' },
      ],
    });

  const handleQuickTopVencidos = () =>
    runAnalysis({
      userText: 'Quais são os 10 clientes com maior valor em contas vencidas?',
      endpoint: '/analytics/top_devedores_vencidos/?limit=10',
      title: 'Top 10 clientes com maior valor em contas vencidas',
      columns: [
        { key: 'nome', label: 'Cliente' },
        { key: 'total_vencido', label: 'Total vencido (R$)', format: 'currency' },
        { key: 'contas_vencidas', label: 'Qtde contas vencidas' },
      ],
    });

  const handleQuickPareto = () =>
    runAnalysis({
      userText: 'Mostre a análise de Pareto (80/20) dos clientes devedores.',
      endpoint: '/analytics/pareto_devedores/?alvo=80',
      title: 'Pareto de devedores (clientes que somam ~80% da dívida)',
      columns: [
        { key: 'nome', label: 'Cliente' },
        { key: 'total_aberto', label: 'Total em aberto (R$)', format: 'currency' },
        { key: 'percentual_acumulado', label: '% acumulado', format: 'percent' },
      ],
      isPareto: true,
    });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const question = input.trim();
    if (!question) return;

    // mostra mensagem do usuário
    addMessage({ role: 'user', type: 'text', text: question });
    setInput('');

    const q = question.toLowerCase();

    // regras simples para mapear perguntas para análises prontas
    if (q.includes('top') && q.includes('devedor')) {
      await handleQuickTopDevedores();
      return;
    }

    if ((q.includes('vencid') || q.includes('atraso')) && q.includes('top')) {
      await handleQuickTopVencidos();
      return;
    }

    if (q.includes('pareto') || q.includes('80/20')) {
      await handleQuickPareto();
      return;
    }

    // se não reconheceu, registra sugestão
    setLoading(true);
    try {
      await api.post('/analytics/sugerir/', {
        pergunta: question,
        contexto: { origem: 'chat-analytics' },
      });
      addMessage({
        role: 'assistant',
        type: 'text',
        text:
          'Ainda não tenho essa análise pronta.\n' +
          'Registrei a sua sugestão para virar um novo relatório no futuro. 👍',
      });
    } catch (error) {
      console.error(error);
      addMessage({
        role: 'assistant',
        type: 'text',
        text: 'Não consegui registrar a sugestão agora. Tente novamente mais tarde.',
      });
    } finally {
      setLoading(false);
    }
  };

  const renderMessage = (msg) => {
    if (msg.type === 'text') {
      return (
        <div className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
          <div
            className={`max-w-2xl whitespace-pre-line px  -4 py-3 rounded-2xl text-sm shadow-sm ${
              msg.role === 'user'
                ? 'bg-blue-600 text-white rounded-br-none'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-bl-none'
            }`}
          >
            {msg.text}
          </div>
        </div>
      );
    }

    if (msg.type === 'analysis') {
      return (
        <div className="flex justify-start">
          <div className="max-w-full w-full md:max-w-4xl bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-2xl rounded-bl-none p-4 shadow-sm">
            <h3 className="text-sm font-semibold mb-2">{msg.title}</h3>

            {msg.meta && (
              <div className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                Total geral:{' '}
                {formatValue(msg.meta.totalGeral, 'currency')} — Coberto:{' '}
                {formatValue(msg.meta.cobertoPercentual, 'percent')}
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="min-w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-gray-200 dark:bg-gray-700">
                    {msg.columns.map((col) => (
                      <th
                        key={col.key}
                        className="px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-200"
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {msg.rows.map((row, index) => (
                    <tr
                      key={index}
                      className={
                        index % 2 === 0
                          ? 'bg-white dark:bg-gray-900'
                          : 'bg-gray-50 dark:bg-gray-800'
                      }
                    >
                      {msg.columns.map((col) => (
                        <td key={col.key} className="px-3 py-2 whitespace-nowrap">
                          {formatValue(row[col.key], col.format)}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {msg.rows.length === 0 && (
                    <tr>
                      <td
                        colSpan={msg.columns.length}
                        className="px-3 py-2 text-center text-gray-500"
                      >
                        Nenhum dado encontrado para este filtro.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="flex flex-col h-full bg-gray-50 dark:bg-gray-900">
      {/* Cabeçalho */}
      <div className="border-b border-gray-200 dark:border-gray-800 px-4 py-3 flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Consultor de Cobrança
          </h1>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Faça perguntas e veja análises com base nos dados reais do seu sistema.
          </p>
        </div>
      </div>

      {/* Área de mensagens */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {messages.map((msg) => (
          <div key={msg.id}>{renderMessage(msg)}</div>
        ))}
        <div ref={bottomRef} />
      </div>

      {/* Input e botões rápidos */}
      <div className="border-top border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-4 py-3">
        <form onSubmit={handleSubmit} className="space-y-2">
          <div className="flex items-end gap-2">
            <textarea
              className="flex-1 resize-none rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={2}
              placeholder="Digite sua pergunta (ex: “Top 10 clientes que mais devem em R$”)..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
            />
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-medium shadow-sm hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Enviando...' : 'Enviar'}
            </button>
          </div>

          <div className="flex flex-wrap gap-2 text-xs">
            <button
              type="button"
              onClick={handleQuickTopDevedores}
              disabled={loading}
              className="px-3 py-1.5 rounded-full bg-gray-200 dark:bg-gray-800 text-gray-800 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-700"
            >
              Top Devedores
            </button>
            <button
              type="button"
              onClick={handleQuickTopVencidos}
              disabled={loading}
              className="px-3 py-1.5 rounded-full bg-gray-200 dark:bg-gray-800 text-gray-800 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-700"
            >
              Top Vencidos
            </button>
            <button
              type="button"
              onClick={handleQuickPareto}
              disabled={loading}
              className="px-3 py-1.5 rounded-full bg-gray-200 dark:bg-gray-800 text-gray-800 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-700"
            >
              Pareto 80/20
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AnalyticsChat;


